'use client';

import { c as _c } from "react/compiler-runtime";
import { TEST_IDS } from '@repo/sdk/constants';
import { Command, CommandDialog, defaultFilter } from '@repo/ui/components';
import { useHotkey } from '@tanstack/react-hotkeys';
import { createContext, type PropsWithChildren, type ReactNode, useContext, useState } from 'react';
import { CommandWorkflowContent } from '../command/command-workflow-content';
import type { CommandWorkflow, CommandWorkflowContext } from '../command/types';
type AdminCmdKContextValue = Readonly<{
  open: AdminCmdKOpen;
}>;
type AdminCmdKOpen<PageId extends string = string> = {
  (): void;
  (_pageId: PageId): void;
};
const adminCmdkFilter = (value: string, search: string, keywords?: string[]) => defaultFilter(value, search.trim(), keywords);
const AdminCmdKContext = createContext<AdminCmdKContextValue | null>(null);
export type AdminCmdKProps<PageId extends string = string> = PropsWithChildren<{
  workflow: CommandWorkflow<PageId>;
}>;
export function AdminCmdK(t0) {
  const $ = _c(20);
  const {
    children,
    workflow
  } = t0;
  const [open, setOpen] = useState(false);
  const [initialPageId, setInitialPageId] = useState(workflow.rootPageId);
  const [session, setSession] = useState(0);
  let t1;
  if ($[0] !== workflow.pages || $[1] !== workflow.rootPageId) {
    t1 = pageId => {
      setInitialPageId(pageId && Object.hasOwn(workflow.pages, pageId) ? pageId : workflow.rootPageId);
      setSession(_temp);
      setOpen(true);
    };
    $[0] = workflow.pages;
    $[1] = workflow.rootPageId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const openCommand = t1;
  let t2;
  if ($[3] !== open || $[4] !== openCommand) {
    t2 = () => {
      if (open) {
        setOpen(false);
        return;
      }
      openCommand();
    };
    $[3] = open;
    $[4] = openCommand;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useHotkey("Mod+K", t2);
  let t3;
  if ($[6] !== openCommand) {
    t3 = {
      open: openCommand
    };
    $[6] = openCommand;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setOpen(false);
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== initialPageId || $[10] !== session || $[11] !== workflow) {
    t5 = <Command filter={adminCmdkFilter} data-sentry-element="Command" data-sentry-source-file="command.tsx"><CommandWorkflowContent close={t4} initialPageId={initialPageId} inputPlaceholder="Type a command or search..." inputTestId={TEST_IDS.cmdk.input} key={session} workflow={workflow} data-sentry-element="CommandWorkflowContent" data-sentry-source-file="command.tsx" /></Command>;
    $[9] = initialPageId;
    $[10] = session;
    $[11] = workflow;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== open || $[14] !== t5) {
    t6 = <CommandDialog className="sm:max-w-xl!" onOpenChange={setOpen} open={open} data-sentry-element="CommandDialog" data-sentry-source-file="command.tsx">{t5}</CommandDialog>;
    $[13] = open;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== children || $[17] !== t3 || $[18] !== t6) {
    t7 = <AdminCmdKContext.Provider value={t3} data-sentry-element="AdminCmdKContext.Provider" data-sentry-component="AdminCmdK" data-sentry-source-file="command.tsx">{t6}{children}</AdminCmdKContext.Provider>;
    $[16] = children;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function _temp(currentSession) {
  return currentSession + 1;
}
export function useAdminCmdK() {
  const context = useContext(AdminCmdKContext);
  if (!context) {
    throw new Error("useAdminCmdK must be used within AdminCmdK");
  }
  return context;
}
export type { CommandWorkflow, CommandWorkflowContext };